var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ResolveResponse, } from '@spotify-internal/remote-config-core';
import { createFinalizationRegistry } from './finalizationRegistry';
import { NanosecondTimer, } from '@spotify-internal/remote-config-common';
const activeExposureTrackers = new WeakMap();
export function sendExposure(configuration) {
    var _a;
    (_a = activeExposureTrackers.get(configuration)) === null || _a === void 0 ? void 0 : _a();
}
export const DEFAULT_GRANULAR_EXPOSURE_OPTIONS = {
    debounceTimeout: 400,
    exposeOnGc: true,
};
export function createExposureMiddleware(sendEventFactory, granularExposureOptions = DEFAULT_GRANULAR_EXPOSURE_OPTIONS, logger) {
    return next => options => {
        const { clientId, semanticMetrics, exposureStrategy = 'access' } = options;
        const sendEventInner = sendEventFactory(options);
        if (exposureStrategy === 'resolve') {
            return onResolveExposureMiddleware(next, clientId, semanticMetrics, sendEventInner, logger);
        }
        if (exposureStrategy === 'access') {
            return onAccessExposureMiddleware(next, clientId, semanticMetrics, sendEventInner, granularExposureOptions, logger);
        }
        throw new Error('exposure strategy not defined');
    };
}
function onResolveExposureMiddleware(next, clientId, semanticMetrics, sendEvent, logger) {
    return (context) => __awaiter(this, void 0, void 0, function* () {
        const configurationBuilder = yield next(context);
        if (semanticMetrics) {
            semanticMetrics.sendMetric({
                metric_type: 'counter',
                what: 'remote-config-exposure-counter',
                tags: { strategy: 'resolve' },
                value: 1,
            });
        }
        const policyGroupIds = unique(Object.values(configurationBuilder.getValue(ResolveResponse.groupIds) || {})).filter(isNumber);
        const exposureEvent = {
            clientId,
            configuration: configurationBuilder,
            policyGroupIds,
            context,
        };
        logger.debug('sending resolve exposure event', exposureEvent);
        sendEvent(exposureEvent);
        return configurationBuilder;
    });
}
function onAccessExposureMiddleware(next, clientId, semanticMetrics, sendEvent, granularExposureOptions, logger) {
    return (context) => __awaiter(this, void 0, void 0, function* () {
        const configurationBuilder = yield next(context);
        const timer = NanosecondTimer.start();
        configurationBuilder.withAccessListener(createGranularExposureListener((policyGroupIds, signal) => {
            if (semanticMetrics) {
                semanticMetrics.sendMetric({
                    metric_type: 'counter',
                    what: 'remote-config-exposure-counter',
                    tags: { strategy: 'access' },
                    value: 1,
                });
                semanticMetrics.sendMetric({
                    metric_type: 'timer',
                    what: 'remote-config-time-to-exposure',
                    tags: { signal },
                    value: timer.duration(),
                });
            }
            const exposureEvent = {
                clientId,
                configuration: configurationBuilder,
                policyGroupIds,
                context,
            };
            logger.debug('sending access exposure event', exposureEvent);
            sendEvent(exposureEvent);
        }, granularExposureOptions, logger));
        return configurationBuilder;
    });
}
export function createGranularExposureListener(sendExposureCallback, options, logger) {
    const { debounceTimeout, exposeOnGc = false } = options;
    const accessedGroupIds = new Set();
    let pendingGroupIds = [];
    let flush = (signal) => {
        logger.debug('Flushing exposure events, with signal %s for pending group ids: %o', signal, pendingGroupIds);
        if (!pendingGroupIds.length)
            return;
        sendExposureCallback(pendingGroupIds, signal);
        pendingGroupIds = [];
    };
    let listener = (ref, config) => {
        const groupId = ResolveResponse.getGroupId(ref, config);
        logger.info('Access listener called for ref: "%s", with group id: "%s"', ref, groupId);
        if (groupId && !accessedGroupIds.has(groupId)) {
            accessedGroupIds.add(groupId);
            pendingGroupIds.push(groupId);
        }
    };
    listener = onEnterOnce(listener, (_, config) => {
        activeExposureTrackers.set(config, () => {
            flush('manual');
        });
    });
    if (debounceTimeout >= 0 && debounceTimeout < Number.POSITIVE_INFINITY) {
        let timeoutId;
        listener = onEnter(listener, () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                flush('timeout');
            }, debounceTimeout);
        });
        flush = onEnter(flush, () => {
            clearTimeout(timeoutId);
        });
    }
    const configFinalizationRegistry = createFinalizationRegistry();
    if (exposeOnGc && configFinalizationRegistry) {
        logger.debug('Adding GC cleanup function');
        listener = onEnterOnce(listener, (_, config) => {
            configFinalizationRegistry.register(config, () => {
                flush('gc');
            });
        });
    }
    return listener;
}
function onEnter(fn, callb) {
    return (...args) => {
        callb(...args);
        return fn(...args);
    };
}
function onEnterOnce(fn, callb) {
    let dispatch = (...args) => {
        dispatch = fn;
        callb(...args);
        return fn(...args);
    };
    return (...args) => {
        return dispatch(...args);
    };
}
function unique(items) {
    return Array.from(new Set(items));
}
function isNumber(value) {
    return typeof value === 'number';
}
//# sourceMappingURL=exposure.js.map